import React, { useState } from "react";
import { ActionsDropdownItem, ActionsMenu } from "@/js/components/ActionsMenu";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import SendNewTabModal from "@/js/components/SendNewTabModal";
import SendMessageModal from "@/js/components/SendMessageModal";
import { getFeatures } from "stores/classwizeConfigStore";
import useStore from "stores/index";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

interface Props {
	openPauseModal: () => void;
	openFocusDrawer: () => void;
	openRewardModal: () => void;
	openBypassModal: () => void;
	openManageStudentsModal: () => void;
	openScreenshareModal: () => void;
	isClassActive: boolean;
	areAnyStudentsFocused: boolean;
	areAllStudentsFocused: boolean;
	studentsArePaused: boolean;
	classHasStudents: boolean;
	classSourceId: string;
}

export default function ClassToolsDropdown(props: Props): JSX.Element {
	const store = useStore();
	const [newTabModalOpen, setNewTabModalOpen] = useState(false);
	const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);

	const userSessionStore = useUserSessionStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	const classwizeFeatures = getFeatures(store);

	const isOpenTabDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isAnnouncementDisabled = !props.isClassActive || !props.classHasStudents;
	const isFocusDisabled = !props.classHasStudents || props.areAnyStudentsFocused;
	const isRewardDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isPauseDisabled = !props.isClassActive || !props.classHasStudents;
	const isModifyStudentsDisabled = false;
	const isBypassCodeDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isScreenShareDisabled = !props.isClassActive || !props.classHasStudents || props.areAllStudentsFocused;

	const focusDisabledTooltip = props.areAnyStudentsFocused ? "Only one Focus can be active within a class" : undefined;
	const screenShareDisabledTooltip = props.areAllStudentsFocused ? "Screenshare unavailable for focused students" : undefined;

	const menuItems: ActionsDropdownItem[] = [
		{
			icon: "fa-browser",
			label: "Open Site",
			dataTestId: "menu-button-open-site",
			show: classwizeFeatures.includes("open_tab"),
			action: () => setNewTabModalOpen(true),
			disabled: isOpenTabDisabled,
			tooltip: isOpenTabDisabled ? undefined : "Open a tab for all students",
		},
		{
			icon: "fa-megaphone",
			label: "Announcement",
			dataTestId: "menu-button-announcement",
			show: classwizeFeatures.includes("message_class"),
			action: () => setAnnouncementModalOpen(true),
			disabled: isAnnouncementDisabled,
			tooltip: isAnnouncementDisabled ? undefined : "Send an announcement to all students in this class",
		},
		{
			icon: "fa-arrows-to-eye",
			label: "Focus",
			dataTestId: "menu-button-focus",
			show: classwizeFeatures.includes("focus"),
			action: props.openFocusDrawer,
			disabled: isFocusDisabled,
			tooltip: isFocusDisabled ? focusDisabledTooltip : "Focus Internet Access",
		},
		{
			icon: "fa-gift",
			label: "Reward",
			dataTestId: "menu-button-reward",
			show: classwizeFeatures.includes("reward"),
			action: props.openRewardModal,
			disabled: isRewardDisabled,
			tooltip: isRewardDisabled ? undefined : "Reward students with less restrictive internet",
		},
		{
			icon: "fa-pause",
			label: "Pause Internet",
			dataTestId: "menu-button-pause",
			show: classwizeFeatures.includes("lock"),
			action: props.openPauseModal,
			disabled: isPauseDisabled,
			tooltip: isPauseDisabled ? undefined : "Lock access to internet",
		},
		{
			icon: "fa-user",
			label: "Modify Students",
			dataTestId: "menu-button-modify-students",
			show: classwizeFeatures.includes("add_user"),
			action: props.openManageStudentsModal,
			disabled: isModifyStudentsDisabled,
			tooltip: isModifyStudentsDisabled ? undefined : "Add a student to this class",
		},
		{
			icon: "fa-key",
			label: "Bypass Code",
			dataTestId: "menu-button-bypass-code",
			show: classwizeFeatures.includes("bypass_code"),
			action: props.openBypassModal,
			disabled: isBypassCodeDisabled,
			tooltip: isBypassCodeDisabled ? undefined : "Bypass codes to allow unfiltered internet",
		},
		{
			icon: "fa-screencast",
			label: "Screen Share",
			dataTestId: "menu-button-screen-share",
			show: !!featureFlagsQuery.data?.includes(FeatureFlag.SCREEN_SHARE),
			action: props.openScreenshareModal,
			disabled: isScreenShareDisabled,
			tooltip: isScreenShareDisabled ? screenShareDisabledTooltip : "Share your screen with your class",
		},
	];

	return (
		<>
			<ActionsMenu title="Class Tools" items={menuItems} />
			<SendNewTabModal
				title={"Open a site for your class"}
				allClassroomStudents={true}
				classroomSourceId={props.classSourceId}
				open={newTabModalOpen}
				onClose={() => setNewTabModalOpen(false)}
			/>
			<SendMessageModal
				title={"Send a class announcement"}
				allClassroomStudents={true}
				classroomSourceId={props.classSourceId}
				open={announcementModalOpen}
				onClose={() => setAnnouncementModalOpen(false)}
				maxMessageLength={500}
				subtitle="Your message will appear on all students' screens. They will not be able to reply."
			/>
		</>
	);
}
